import { useDataProvider } from "src/system/useDataProvider.ts";
import {
  type DefaultError,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  GridFilterModel,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import type { ValidGetListResponse } from "src/system/SystemProvider.tsx";
import { logger } from "src/logger";
import { useGlobalSettings } from "src/hooks/use-global-settings.ts";
import { usePrevious } from "react-use";
import { useEffect } from "react";
import { deepEq } from "@exonn/utils";

type UseListProps<Row extends GridValidRowModel = GridValidRowModel> = {
  resource: string;
  option?: string;
  filter?: GridFilterModel;
  groupKeys?: string[];
  include?: string[];
  sort?: GridSortModel;
  onQueryFn?: () => void;
  page?: {
    size: number;
    number: number;
  };
  withAggregation?: boolean;
  disablePagination?: boolean;
  invalidatePreviousQuery?: boolean;
  useSimplePagination?: boolean;
  locale?: string;
  queryOptions?: Omit<
    UseQueryOptions<
      ValidGetListResponse<Row>,
      DefaultError,
      ValidGetListResponse<Row>,
      any
    >,
    "queryKey" | "queryFn"
  >;
};

export const useList = <Row extends GridValidRowModel = GridValidRowModel>(
  props: UseListProps<Row>,
): UseQueryResult<ValidGetListResponse<Row>, DefaultError> => {
  const {
    resource,
    filter,
    sort,
    page: pageProp,
    locale: localeProp,
    withAggregation,
    groupKeys,
    useSimplePagination = false,
    include,
    onQueryFn,
    option,
    queryOptions,
    invalidatePreviousQuery,
    disablePagination,
  } = props;
  const { getList } = useDataProvider();

  const {
    data: { default_language },
  } = useGlobalSettings();

  const defaultLocale = default_language?.locale;
  const queries = useResourceQueries();
  const locale = localeProp || defaultLocale || "de";
  const page = disablePagination
    ? {
        number: 1,
        size: 999999,
      }
    : pageProp;
  const queryKey = queries.list({
    filter,
    sort,
    page,
    include,
    withAggregation,
    useSimplePagination,
    option,
    groupKeys,
    locale,
    resource,
  });

  const queryClient = useQueryClient();

  const previousQueryKey = usePrevious(queryKey);

  useEffect(() => {
    if (
      invalidatePreviousQuery &&
      previousQueryKey &&
      !deepEq(previousQueryKey, queryKey)
    ) {
      queryClient.invalidateQueries({
        queryKey: previousQueryKey,
      });
    }
  }, [previousQueryKey, queryKey, invalidatePreviousQuery, queryClient]);

  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        onQueryFn?.();
        const params = {
          resource,
          signal,
          filter,
          groupKeys,
          include,
          withAggregation,
          useSimplePagination,
          sort,
          page,
          locale,
          option,
        };
        return (await getList(params)) as ValidGetListResponse<Row>;
      } catch (e: any) {
        logger.error(e);
        throw e;
      }
    },
    ...queryOptions,
  }) as UseQueryResult<ValidGetListResponse<Row>, DefaultError>;
};
